import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Youtube, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BRAND, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success("You're subscribed. Thank you.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-navy text-white">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-28">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-4xl font-light leading-[1.05] tracking-tight md:text-5xl">
              Let&rsquo;s talk about the <span className="font-black text-sky">decision</span> in front of you.
            </h2>
            <p className="mt-6 max-w-md font-body text-lg leading-relaxed text-slate-300">
              A confidential conversation about your priorities, and an honest view of where technology creates value, and where it does not.
            </p>
            <Link
              to="/book"
              data-testid="footer-book-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-royal px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-sky hover:text-navy"
            >
              Book a Strategy Session
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-8 lg:col-span-4 lg:col-start-8">
            <div>
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Advisory</p>
              <ul className="mt-5 space-y-3">
                {SERVICES.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="font-body text-sm text-slate-300 transition-colors hover:text-white">
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Explore</p>
              <ul className="mt-5 space-y-3">
                {[
                  ["About", "/about"], ["Industries", "/industries"], ["Executive Success Stories", "/case-studies"],
                  ["Insights", "/insights"], ["Speaking", "/videos"], ["Briefings", "/resources"], ["Contact", "/contact"],
                ].map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="font-body text-sm text-slate-300 transition-colors hover:text-white">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 grid gap-8 border-t border-white/10 pt-10 md:grid-cols-2">
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Newsletter</p>
            <form onSubmit={subscribe} className="mt-4 flex max-w-md gap-3">
              <input
                data-testid="footer-newsletter-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="w-full rounded-full border border-white/15 bg-white/5 px-5 py-3 font-body text-sm text-white placeholder:text-slate-400 focus:border-sky focus:outline-none focus:ring-2 focus:ring-sky/30"
              />
              <button
                data-testid="footer-newsletter-submit"
                disabled={busy}
                className="whitespace-nowrap rounded-full bg-white px-6 py-3 font-body text-sm font-semibold text-navy transition-colors hover:bg-sky disabled:opacity-60"
              >
                {busy ? "…" : "Subscribe"}
              </button>
            </form>
          </div>
          <div className="flex flex-col justify-end md:items-end">
            <div className="flex gap-3">
              {[[Linkedin, BRAND.socials.linkedin, "linkedin"], [Twitter, BRAND.socials.x, "x"], [Youtube, BRAND.socials.youtube, "youtube"]]
                .filter(([, href]) => href && href !== "#")
                .map(
                ([Icon, href, key]) => (
                  <a
                    key={key}
                    href={href}
                    data-testid={`footer-social-${key}`}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-white hover:text-white"
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </a>
                )
              )}
            </div>
            <p className="mt-4 font-body text-sm text-slate-400">{BRAND.email}</p>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-8 font-body text-sm text-slate-400 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-white">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-white">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
